import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { paymentRecoveryService } from '../../lib/paymentRecoveryService';


import FailedPaymentsDashboard from './components/FailedPaymentsDashboard';
import RetryScheduleManager from './components/RetryScheduleManager';
import DunningManagement from './components/DunningManagement';
import ChurnRiskMonitor from './components/ChurnRiskMonitor';
import ChurnPreventionWorkflows from './components/ChurnPreventionWorkflows';
import RecoveryAnalytics from './components/RecoveryAnalytics';

const PaymentRecoveryRetentionCenter = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [failedPayments, setFailedPayments] = useState([]);
  const [churnRiskMembers, setChurnRiskMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const gymId = userProfile?.gym_id;

  useEffect(() => {
    if (gymId) {
      loadDashboardData();
    }
  }, [gymId, refreshTrigger]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load failed payments
      const { data: paymentsData } = await paymentRecoveryService?.getFailedPaymentsDashboard(
        gymId,
        { isResolved: false, limit: 50 }
      );
      setFailedPayments(paymentsData || []);

      // Load churn risk members
      const { data: churnData } = await paymentRecoveryService?.getMemberChurnRisk(
        gymId,
        { limit: 30 }
      );
      setChurnRiskMembers(churnData || []);

      // Load analytics
      const { data: analyticsData } = await paymentRecoveryService?.getPaymentRecoveryAnalytics(
        gymId,
        { 
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
          endDate: new Date()?.toISOString()?.split('T')?.[0]
        }
      );
      
      if (analyticsData && analyticsData?.length > 0) {
        const latestAnalytics = analyticsData?.[0];
        setAnalytics(latestAnalytics);
      } else {
        // Mock analytics if no data
        setAnalytics({
          total_failed_payments: failedPayments?.length || 0,
          total_failed_amount: failedPayments?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0) || 0,
          successful_recoveries: 0,
          recovered_amount: 0,
          recovery_rate: 0,
          avg_retry_attempts: 0,
          dunning_notifications_sent: 0,
          dunning_response_rate: 0,
          churn_prevented_count: 0,
          churn_prevented_value: 0
        });
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    { id: 'dashboard', label: 'Failed Payments', icon: 'AlertCircle' },
    { id: 'retry', label: 'Retry Engine', icon: 'RefreshCw' },
    { id: 'dunning', label: 'Dunning Management', icon: 'MessageSquare' },
    { id: 'churn', label: 'Churn Risk', icon: 'TrendingDown' },
    { id: 'prevention', label: 'Prevention Workflows', icon: 'Shield' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  const priorityStats = [
    {
      label: 'Overdue Accounts',
      value: failedPayments?.filter(p => !p?.is_resolved)?.length || 0,
      icon: 'AlertTriangle',
      color: 'red',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600'
    },
    {
      label: 'Recovery Rate',
      value: `${analytics?.recovery_rate?.toFixed(1) || 0}%`,
      icon: 'TrendingUp',
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      label: 'At-Risk Members',
      value: churnRiskMembers?.filter(m => m?.risk_level === 'high' || m?.risk_level === 'critical')?.length || 0,
      icon: 'Users',
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      label: 'Recovered Amount',
      value: `$${analytics?.recovered_amount?.toFixed(2) || '0.00'}`,
      icon: 'DollarSign',
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <DashboardNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-gray-600">Loading payment recovery data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment Recovery & Retention Center | PulseLoom</title>
        <meta name="description" content="Comprehensive failed payment management with automated retry scheduling and churn prevention workflows" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <DashboardNavigation />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Payment Recovery & Retention Center</h1>
                <p className="text-gray-600 mt-2">
                  Automated retry scheduling, dunning notifications, and churn prevention workflows
                </p>
              </div>
              <Button
                iconName="RefreshCw"
                onClick={handleRefresh}
                variant="outline"
              >
                Refresh Data
              </Button>
            </div>
          </div>

          {/* Priority Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {priorityStats?.map((stat) => (
              <div key={stat?.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{stat?.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat?.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
                    <Icon name={stat?.icon} className={`w-6 h-6 ${stat?.iconColor}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {activeTab === 'dashboard' && (
              <FailedPaymentsDashboard 
                failedPayments={failedPayments}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === 'retry' && (
              <RetryScheduleManager 
                failedPayments={failedPayments}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === 'dunning' && (
              <DunningManagement 
                failedPayments={failedPayments}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === 'churn' && (
              <ChurnRiskMonitor 
                churnRiskMembers={churnRiskMembers}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === 'prevention' && (
              <ChurnPreventionWorkflows 
                churnRiskMembers={churnRiskMembers}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === 'analytics' && (
              <RecoveryAnalytics 
                analytics={analytics}
                gymId={gymId}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentRecoveryRetentionCenter;